import { Job, RetryableJob } from "~/components/build/Show/lib/types";
import { filterRetriesForJob } from "~/components/build/Show/components/StepDrawer/StepDrawerContext";
import { QueryableJobsCollection } from "~/components/build/Show/lib/jobs";

/**
 * The past (superseded) attempts for a job, in retry-chain order (oldest first)
 * and excluding the latest attempt — which is the row the history hangs off.
 *
 * `stepJobs` is the full set of jobs loaded for a step. For parallel/matrix
 * steps `filterRetriesForJob` narrows the set to the lane the job belongs to.
 *
 * The latest attempt is identified by walking the `retriedInJobUuid` chain (the
 * tail has no successor), not by array position — the loaded jobs are not
 * reliably ordered, so trusting their order would surface the latest job as the
 * first "attempt" row.
 */
export function pastAttemptsForJob(
  stepJobs: RetryableJob[],
  referenceJob: RetryableJob,
): RetryableJob[] {
  const lane = filterRetriesForJob(stepJobs, referenceJob);
  if (lane.length <= 1) {
    return [];
  }

  // The tail of the chain (no `retriedInJobUuid`) is the current attempt.
  const latest = lane.find((job) => !job.retriedInJobUuid) ?? lane[lane.length - 1];

  return new QueryableJobsCollection(lane as Job[]).getPriorJobsInRetryChain(
    latest as Job,
  ) as RetryableJob[];
}
